import { memo } from 'react'

import { Button, Text } from '@cash-me/react-components'
import Image from 'next/image'

import { Breadcrumb } from 'components/breadcrumb'
import { Infos } from 'components/general-styles/General'
import { Col, Grid } from 'components/grid'
import { CalendarIcon, HandshakeIcon, MoneyRotationIcon } from 'components/new-icons'
import { Polygon } from 'components/polygon'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'

import * as S from './styles'

type Props = {
  onCtaClick: () => void
}

export const CondominiumCreditHero = memo(({ onCtaClick }: Props) => {
  const { isTablet } = useWindowSize()

  return (
    <S.Section id="hero-section" aria-labelledby="condominium-credit-hero-title">
      <Grid col={{ phone: 1, tablet: 2, desktop: 12 }}>
        <Col col={{ desktop: 5 }} start={{ desktop: 2 }}>
          <Infos>
            <Breadcrumb items={[{ label: 'Crédito para Condomínios' }]} />

            <Text
              id="condominium-credit-hero-title"
              as="h1"
              size="heading2"
              weight="medium"
              color="var(--colors-neutral-text-default)"
            >
              Crédito para <S.Highlight>Condomínios</S.Highlight>
            </Text>

            <Text color="var(--colors-neutral-text-default)">
              Realize as melhorias que o seu empreendimento precisa. Com o{' '}
              <b>Crédito para Condomínios da CashMe</b>, o empreendimento pode obter recursos para colocar projetos
              importantes em prática e organizar o pagamento ao longo do tempo.
            </Text>

            <S.LoanInfos aria-label="Condições do Crédito para Condomínios">
              <S.LoanInfosItem>
                <CalendarIcon color="var(--colors-cyan-icon-hover)" />
                <Text>Até 90 meses para pagar</Text>
              </S.LoanInfosItem>
              <S.LoanInfosItem>
                <MoneyRotationIcon color="var(--colors-cyan-icon-hover)" />
                <Text>Até 3 meses de carência</Text>
              </S.LoanInfosItem>
              <S.LoanInfosItem>
                <HandshakeIcon color="var(--colors-cyan-icon-hover)" />
                <Text>Sem fiador ou avalista</Text>
              </S.LoanInfosItem>
            </S.LoanInfos>

            <Button
              aria-label="Solicitar uma simulação de crédito para condomínios"
              size="lg"
              type="button"
              variants="text-only"
              onClick={onCtaClick}
            >
              <span>Solicitar uma simulação</span>
            </Button>
          </Infos>
        </Col>

        {isTablet && (
          <Col start={{ desktop: 7 }} col={{ tablet: 6, desktop: 5 }}>
            <S.ImageWrapper>
              <Image
                src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/bacen/condo.webp`}
                alt="Fachada de um condomínio residencial"
                width={1053}
                height={1151}
                quality={90}
                priority
                sizes="(min-width: 1240px) 40vw, 50vw"
                style={{ width: '100%', height: 'auto' }}
              />
              <Polygon
                aria-hidden
                type="TriangleRound"
                width={80}
                height={100}
                draw="fill"
                color={theme.colors['cashme-magenta']}
                className="background-polygon polygon-pink"
              />
            </S.ImageWrapper>
          </Col>
        )}
      </Grid>
    </S.Section>
  )
})

CondominiumCreditHero.displayName = 'CondominiumCreditHero'
